/** Bounded least-recently-used map for module-level warm-start caches. */
export class LruMap<K, V> {
  private readonly maxEntries: number;
  private readonly map = new Map<K, V>();

  constructor(maxEntries: number) {
    this.maxEntries = maxEntries;
  }

  get size(): number {
    return this.map.size;
  }

  /** Returns the value and marks the key as most recently used. */
  get(key: K): V | undefined {
    const value = this.map.get(key);

    if (value === undefined) {
      return undefined;
    }

    this.map.delete(key);
    this.map.set(key, value);

    return value;
  }

  /** Inserts or updates an entry, evicting the oldest when at capacity. */
  set(key: K, value: V): void {
    if (this.map.has(key)) {
      this.map.delete(key);
    } else if (this.map.size >= this.maxEntries) {
      const oldestKey = this.map.keys().next().value;

      if (oldestKey !== undefined) {
        this.map.delete(oldestKey);
      }
    }

    this.map.set(key, value);
  }

  clear(): void {
    this.map.clear();
  }
}
